/**
 * What a failed tool call shows in the chat (web `fallback-card.tsx` and the
 * native `FallbackCard` in `index.native.tsx`): one plain sentence, and a
 * Details disclosure, collapsed, with the structured error as readable rows.
 * Raw JSON is never what the reader sees first.
 *
 * Every shape a failure arrives in goes through here:
 * - `{ ok: false, error: { code, status, message, details } }` (the audited
 *   tools, `TOOL_INPUT_INVALID` among them);
 * - `{ ok: false, code, message, details }` (a refused page, which also
 *   carries its card fields);
 * - Mastra's own `{ error: true, message }` and plain strings, from threads
 *   saved before the server answered invalid arguments in words.
 *
 * The server writes `error.message` in the user's language (en or ar), so the
 * sentence is its first sentence; the rest of a long message is guidance for
 * the model and goes into Details. When there is no usable message the card
 * shows its own localized line (`fallbackKey`).
 */

export type ToolFailureRow = {
  /** A copy key for the fixed labels (code, status), else the field's own name. */
  labelKey?: 'code' | 'status';
  label: string;
  value: string;
};

export type ToolFailure = {
  /** The sentence to show, or null: then the card shows `fallbackKey`. */
  sentence: string | null;
  /** The localized line under `chat.toolResult` when `sentence` is null. */
  fallbackKey: 'failed' | 'inputInvalid';
  code: string | null;
  status: number | null;
  /** Details, formatted; empty means no disclosure. */
  rows: ToolFailureRow[];
};

/** Rows in Details at most, and characters per value. */
export const TOOL_FAILURE_LIMITS = { rows: 14, value: 240, sentence: 280 } as const;

/** Mastra's own words for arguments that failed the schema (older threads). */
const MASTRA_INPUT_INVALID = /^Tool input validation failed/i;

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function clip(value: string, max: number): string {
  const flat = value.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1).trimEnd()}…` : flat;
}

/**
 * The first sentence of a message: up to the first `.`, `!`, `?` or `؟`
 * that ends a sentence (followed by a space or the end), so "6,000" and
 * "tabbio.site" stay whole.
 */
export function firstSentence(message: string): string {
  const flat = message.replace(/\s+/g, ' ').trim();
  const match = /[.!?؟](?=\s|$)/.exec(flat);
  const sentence = match ? flat.slice(0, match.index + 1) : flat;
  return clip(sentence, TOOL_FAILURE_LIMITS.sentence);
}

/** "instruction: String must contain at most 2000 character(s)" lines of Mastra's message. */
function mastraIssueRows(message: string): ToolFailureRow[] {
  const rows: ToolFailureRow[] = [];
  const body = message.split(/Provided arguments:/i)[0] ?? '';
  for (const line of body.split('\n')) {
    const match = /^\s*-\s*([^:]+):\s*(.+)$/.exec(line);
    if (match) rows.push({ label: match[1]!.trim(), value: clip(match[2]!, TOOL_FAILURE_LIMITS.value) });
  }
  return rows;
}

function scalar(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() ? clip(value, TOOL_FAILURE_LIMITS.value) : null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  return null;
}

/** One readable line for an item of a list: its message, else its scalar fields. */
function itemLine(item: unknown): string | null {
  const direct = scalar(item);
  if (direct !== null) return direct;
  const source = record(item);
  if (!source) return null;
  const message = text(source.message);
  if (message) return clip(message, TOOL_FAILURE_LIMITS.value);
  const parts = Object.entries(source)
    .map(([key, value]) => {
      const shown = scalar(value);
      return shown === null ? null : `${key} ${shown}`;
    })
    .filter((part): part is string => part !== null);
  return parts.length > 0 ? clip(parts.join(', '), TOOL_FAILURE_LIMITS.value) : null;
}

/** A finding's or an issue's own name for its row: the rule or the field path. */
function itemLabel(item: unknown, fallback: string): string {
  const source = record(item);
  if (!source) return fallback;
  const rule = text(source.ruleId);
  if (rule) return rule.replace(/^tabbio\//, '');
  const path = source.path;
  if (Array.isArray(path) && path.length > 0) return path.map(String).join('.');
  const named = text(path) ?? text(source.field) ?? text(source.locator);
  return named ?? fallback;
}

/** Details as rows: lists one row per item, objects one level deep, scalars as they are. */
function detailRows(details: unknown, prefix = ''): ToolFailureRow[] {
  const source = record(details);
  if (!source) return [];
  const rows: ToolFailureRow[] = [];
  for (const [key, value] of Object.entries(source)) {
    const name = prefix ? `${prefix}.${key}` : key;
    const shown = scalar(value);
    if (shown !== null) {
      rows.push({ label: name, value: shown });
      continue;
    }
    if (Array.isArray(value)) {
      const lines = value.map((item) => ({ item, line: itemLine(item) })).filter((entry) => entry.line !== null);
      if (lines.length === 0) continue;
      if (lines.every((entry) => typeof entry.item !== 'object' || entry.item === null)) {
        rows.push({ label: name, value: clip(lines.map((entry) => entry.line).join(', '), TOOL_FAILURE_LIMITS.value) });
        continue;
      }
      for (const entry of lines) rows.push({ label: itemLabel(entry.item, name), value: entry.line as string });
      continue;
    }
    if (record(value) && !prefix) rows.push(...detailRows(value, name));
  }
  return rows;
}

/**
 * Reads a failed tool result. A result with no usable words gives no
 * sentence (the card shows its localized line) and, with no code, no rows.
 */
export function readToolFailure(result: unknown): ToolFailure {
  const failure: ToolFailure = { sentence: null, fallbackKey: 'failed', code: null, status: null, rows: [] };

  const raw = typeof result === 'string' ? result : null;
  const source = record(result);
  const nested = record(source?.error);
  const message =
    raw ?? text(nested?.message) ?? text(source?.message) ?? text(record(source?.failure)?.message) ?? null;
  const code = text(nested?.code) ?? text(source?.code) ?? text(record(source?.failure)?.code);
  const status = nested?.status ?? source?.status;
  failure.code = code;
  failure.status = typeof status === 'number' && Number.isFinite(status) ? status : null;

  if (message && MASTRA_INPUT_INVALID.test(message.trim())) {
    // Mastra's developer text (and the arguments it echoes) never shows.
    failure.fallbackKey = 'inputInvalid';
    failure.rows = mastraIssueRows(message);
  } else if (message) {
    failure.sentence = firstSentence(message);
    const rest = message.replace(/\s+/g, ' ').trim().slice(failure.sentence.length).trim();
    const details = detailRows(nested?.details ?? source?.details ?? record(source?.failure)?.details);
    failure.rows = details;
    if (rest) failure.rows.push({ label: 'message', value: clip(rest, TOOL_FAILURE_LIMITS.value) });
  }

  const head: ToolFailureRow[] = [];
  if (failure.code) head.push({ labelKey: 'code', label: 'code', value: failure.code });
  if (failure.status !== null) head.push({ labelKey: 'status', label: 'status', value: String(failure.status) });
  failure.rows = [...head, ...failure.rows].slice(0, TOOL_FAILURE_LIMITS.rows);
  return failure;
}
